import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormTagSelect } from "@/components/Form/FormTagSelect";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import type { Specification } from "@/modules/bookings/constants";
import { specificationOptions } from "@/modules/bookings/constants";
import { useTranslation } from "@/translations";

const FORM_ID = "extra-specifications-modal-form";

type ExtraSpecificationsModalProps = ModalBaseProps & {
  availableSpecifications: Specification[];
  onAddSpecifications: (specifications: Specification[]) => void;
};

interface FormValues {
  specifications: Specification[];
}

export function ExtraSpecificationsModal({
  isOpened,
  onOpenChange,
  availableSpecifications,
  onAddSpecifications,
}: ExtraSpecificationsModalProps): React.ReactNode {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    defaultValues: {
      specifications: availableSpecifications,
    },
  });

  function onSubmit() {
    onAddSpecifications(form.getValues("specifications"));
    onOpenChange(false);
  }

  const getOptionLabel = (specification: Specification) => {
    switch (specification) {
      case "sizeSpecification":
        return t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.modal.option.size");
      case "pricePerHourSpecification":
        return t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.modal.option.price");
      case "capacitySpecification":
        return t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.modal.option.capacity");
      case "locationSpecification":
        return t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.modal.option.location");
    }
  };

  useEffect(() => {
    form.reset({
      specifications: availableSpecifications,
    });
  }, [form, availableSpecifications]);

  return (
    <Modal.Root
      title={t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.modal.title")}
      description={t("page.bookings.create-or-edit.form.section.asset-details.extra-specifications.modal.description")}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={onSubmit}>
          <FormTagSelect<FormValues, "specifications">
            name="specifications"
            items={specificationOptions.map((specificationOption) => ({
              label: getOptionLabel(specificationOption),
              value: specificationOption,
              "data-testid": `${specificationOption}-btn`,
            }))}
            isMultiple
          />
        </Form.Root>
      </Modal.Body>

      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary">
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button form={FORM_ID} data-testid="confirm-extra-specifications" variant="filled" type="submit">
          {t("common.action.add")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
